import React, { forwardRef, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, BadgeEuro, Building, FileCheck, Radar, ScanSearch, Search,
  UserRoundCheck, UserSearch, Users, Phone,
} from 'lucide-react';
import { BLOG_POSTS, KONTAKT } from '../constants';
import { srcSet } from '../lib/bilder';
import { BERUFE } from '../data/berufe/kurz';
import { AUTOREN } from '../components/blog/AuthorBio';
import { berufIcon } from '../components/berufe/icons';
import { Container, Eyebrow, SectionHeading, ButtonLink, ArrowLink } from '../components/site/ui';
import { AnimatedGridPattern } from '../components/ui/animated-grid-pattern';
import { AnimatedShinyText } from '../components/ui/animated-shiny-text';
import { AuroraText } from '../components/ui/aurora-text';
import { ShimmerButton } from '../components/ui/shimmer-button';
import { Terminal, TypingAnimation, AnimatedSpan } from '../components/ui/terminal';
import { Marquee } from '../components/ui/marquee';
import { NumberTicker } from '../components/ui/number-ticker';
import { BentoGrid, BentoCard } from '../components/ui/bento-grid';
import { AnimatedBeam } from '../components/ui/animated-beam';
import { MagicCard } from '../components/ui/magic-card';
import { BlurFade } from '../components/ui/blur-fade';
import { BorderBeam } from '../components/ui/border-beam';
import { RetroGrid } from '../components/ui/retro-grid';
import { DotPattern } from '../components/ui/dot-pattern';

const d = (ms: number) => ({ '--d': `${ms}ms` } as React.CSSProperties);
const k = (n: number) => `${Math.round(n / 1000)}k`;

const STACK = [
  'Kubernetes', 'Terraform', 'Go', 'Kotlin', 'Java', 'TypeScript', 'React', 'Python', 'PyTorch',
  'AWS', 'Azure', 'Google Cloud', 'STACKIT', 'PostgreSQL', 'Kafka', 'Swift', 'Flutter',
  'Microsoft Sentinel', 'Splunk', 'Databricks', 'dbt', 'Figma',
];

// Belegbare Angaben aus den Konditionen und den Lebensläufen der Inhaber
const FAKTEN = [
  { wert: 25, suffix: ' %', label: 'Honorar im Regelfall, erst bei Einstellung fällig' },
  { wert: 15, prefix: 'ab ', suffix: ' %', label: 'für kleinere Unternehmen oder Pauschale' },
  { wert: 2013, start: 2000, prefix: 'seit ', label: 'IT-Besetzungen mit djiiga in Berlin' },
  { wert: 30, suffix: '+', label: 'Jahre Recruiting-Erfahrung der beiden Inhaber' },
];

// Derselbe Ablauf wie auf der Headhunter-Seite, es gibt nur einen.
const ABLAUF = [
  { titel: 'Briefing', text: 'Etwa eine Stunde mit der Führungskraft, nicht nur mit HR: Aufgaben, Tech-Stack, Team, Gehaltsband.' },
  { titel: 'Marktabgleich', text: 'Wir sagen früh, wenn Anforderungen und Gehaltsband im Berliner Markt nicht zusammenpassen.' },
  { titel: 'Direktansprache', text: 'Persönlich und mit Bezug zur konkreten Rolle. Keine Serienmails an Verteilerlisten.' },
  { titel: 'Vorauswahl', text: 'Wir sprechen mit jedem Kandidaten, bevor wir ihn vorstellen. Sie bekommen eine Auswahl mit Einschätzung.' },
  { titel: 'Begleitung', text: 'Termine, Feedback in beide Richtungen, Angebot. In dieser Phase scheitern die meisten Prozesse.' },
];

const Knoten = forwardRef<HTMLDivElement, { children: React.ReactNode; className?: string; label: string; seitlich?: boolean }>(
  ({ children, className = '', label, seitlich }, ref) => (
    <div className={`flex items-center gap-2 ${seitlich ? 'flex-row' : 'flex-col'}`}>
      <div ref={ref} className={`z-10 flex size-12 items-center justify-center rounded-xl border border-line-2 bg-surface-2 shadow-[0_0_30px_-10px_rgb(34_211_238/.5)] ${className}`}>
        {children}
      </div>
      <span className="font-mono text-[.6rem] uppercase tracking-[.12em] text-muted">{label}</span>
    </div>
  ),
);
Knoten.displayName = 'Knoten';

/** Unternehmen ↔ djiiga ↔ Menschen, die gerade nicht suchen */
const DirektanspracheBeam: React.FC = () => {
  const box = useRef<HTMLDivElement>(null);
  const firma = useRef<HTMLDivElement>(null);
  const mitte = useRef<HTMLDivElement>(null);
  const k1 = useRef<HTMLDivElement>(null);
  const k2 = useRef<HTMLDivElement>(null);
  const k3 = useRef<HTMLDivElement>(null);
  return (
    <div ref={box} className="absolute inset-x-0 top-0 flex h-52 items-center md:h-[52%] justify-between pr-6 pl-8 sm:pr-24 sm:pl-12" aria-hidden="true">
      <Knoten ref={firma} label="Unternehmen"><Building className="size-5 text-ink" /></Knoten>
      <Knoten ref={mitte} label="djiiga" className="size-14 border-primary/50"><Radar className="size-6 text-primary" /></Knoten>
      <div className="flex flex-col gap-2.5">
        <Knoten ref={k1} seitlich label="sucht nicht" className="size-9 rounded-lg"><UserSearch className="size-4 text-muted" /></Knoten>
        <Knoten ref={k2} seitlich label="zufrieden" className="size-9 rounded-lg"><Users className="size-4 text-muted" /></Knoten>
        <Knoten ref={k3} seitlich label="passt" className="size-9 rounded-lg"><UserRoundCheck className="size-4 text-signal" /></Knoten>
      </div>
      <AnimatedBeam containerRef={box} fromRef={firma} toRef={mitte} gradientStartColor="#22d3ee" gradientStopColor="#8b5cf6" pathColor="#263142" pathOpacity={1} duration={4} />
      <AnimatedBeam containerRef={box} fromRef={mitte} toRef={k1} curvature={40} gradientStartColor="#22d3ee" gradientStopColor="#8b5cf6" pathColor="#263142" pathOpacity={1} duration={4} delay={0.4} />
      <AnimatedBeam containerRef={box} fromRef={mitte} toRef={k2} gradientStartColor="#22d3ee" gradientStopColor="#8b5cf6" pathColor="#263142" pathOpacity={1} duration={4} delay={0.8} />
      <AnimatedBeam containerRef={box} fromRef={mitte} toRef={k3} curvature={-40} gradientStartColor="#34d399" gradientStopColor="#22d3ee" pathColor="#263142" pathOpacity={1} duration={4} delay={1.2} />
    </div>
  );
};

const Home: React.FC = () => {
  const posts = BLOG_POSTS.slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="relative -mt-16 overflow-hidden pt-16">
        <div className="glow-top pointer-events-none absolute inset-0" aria-hidden="true" />
        <AnimatedGridPattern
          numSquares={36}
          maxOpacity={0.18}
          duration={3}
          width={56}
          height={56}
          className="mask-radial inset-x-0 h-full fill-primary/40 stroke-white/[.06] text-primary/40"
        />
        <Container className="relative pt-16 md:pt-24">
          <div className="hero-in inline-flex items-center gap-2 rounded-full border border-line-2 bg-white/[.03] px-3.5 py-1.5 backdrop-blur">
            <span className="pulse-dot size-2 rounded-full bg-signal" aria-hidden="true" />
            <AnimatedShinyText className="font-mono text-xs text-muted">
              IT-Recruiting · Berlin · inhabergeführt seit 2013
            </AnimatedShinyText>
          </div>

          <h1 className="hero-in mt-7 max-w-5xl text-[2.5rem] font-semibold leading-[1.04] text-white sm:text-6xl lg:text-[4.4rem]" style={d(80)}>
            <span className="text-gradient">IT Personalvermittlung</span> in Berlin.
            <br className="hidden md:block" />{' '}
            Wir erreichen die Leute,{' '}
            <AuroraText colors={['#22d3ee', '#38bdf8', '#8b5cf6', '#22d3ee']}>die nicht suchen.</AuroraText>
          </h1>
        </Container>

        <Container className="relative grid items-start gap-14 pt-10 pb-20 md:pb-28 lg:grid-cols-[1fr_1.05fr]">
          <div>
            <p className="hero-in max-w-xl text-lg leading-relaxed text-muted" style={d(160)}>
              Wir besetzen Positionen in Software, Cloud, Data, IT-Security und KI. Für Festanstellung,
              Freelance und Interim Management, in Berlin und deutschlandweit.
            </p>

            <div className="hero-in mt-9 flex flex-wrap items-center gap-3" style={d(240)}>
              <ShimmerButton
                to="/fuer-unternehmen"
                background="#22d3ee"
                shimmerColor="#ffffff"
                borderRadius="999px"
                shimmerDuration="2.8s"
                className="!text-on-primary px-7 py-3.5 font-semibold shadow-[0_10px_40px_-10px_rgb(34_211_238/.7)]"
              >
                <span className="relative z-10 inline-flex items-center gap-2">Für Unternehmen <ArrowRight className="size-4" aria-hidden="true" /></span>
              </ShimmerButton>
              <ButtonLink to="/fuer-kandidaten" variant="secondary">Für Kandidaten</ButtonLink>
            </div>

            <ul className="hero-in mt-9 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs text-muted" style={d(320)}>
              <li className="flex items-center gap-2"><BadgeEuro className="size-4 text-primary" aria-hidden="true" />25 % im Regelfall, offen genannt</li>
              <li className="flex items-center gap-2"><FileCheck className="size-4 text-primary" aria-hidden="true" />Konditionen vorab schriftlich</li>
              <li className="flex items-center gap-2"><UserRoundCheck className="size-4 text-primary" aria-hidden="true" />Für Kandidaten kostenfrei</li>
            </ul>
          </div>

          {/* Beispielablauf als Terminal */}
          <div className="hero-in relative min-w-0" style={d(360)} aria-hidden="true">
            <div className="absolute -inset-6 rounded-[2rem] bg-primary/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-2xl">
              <Terminal className="!max-w-none min-h-[19rem] !border-line-2 !bg-surface/90 backdrop-blur-xl font-mono shadow-[0_40px_80px_-40px_rgb(0_0_0/.8)]">
                <TypingAnimation className="text-primary" duration={28}>{'$ djiiga briefing --rolle "Senior Platform Engineer"'}</TypingAnimation>
                <AnimatedSpan className="text-signal">✔ Tech-Stack, Team und Gehaltsband geklärt</AnimatedSpan>
                <AnimatedSpan className="text-signal">✔ Marktabgleich: 3 Muss-Kriterien statt 9</AnimatedSpan>
                <TypingAnimation className="text-primary" duration={28}>$ djiiga direktansprache --persönlich</TypingAnimation>
                <AnimatedSpan className="text-muted">→ Menschen, die gerade nicht suchen</AnimatedSpan>
                <AnimatedSpan className="text-signal">✔ Vorauswahl: Profile mit Einschätzung</AnimatedSpan>
                <TypingAnimation className="text-primary" duration={28}>$ djiiga angebot --begleiten</TypingAnimation>
                <AnimatedSpan className="text-white">✔ Vertrag unterschrieben. Honorar erst jetzt fällig.</AnimatedSpan>
              </Terminal>
              <BorderBeam size={140} duration={8} colorFrom="#22d3ee" colorTo="#8b5cf6" />
            </div>
            <p className="mt-3 text-right font-mono text-[.65rem] uppercase tracking-[.14em] text-subtle">Beispielablauf einer Suche</p>
          </div>
        </Container>

        {/* Stacks */}
        <div className="relative border-y border-line bg-surface/40 py-5">
          <p className="sr-only">Technologien, in denen wir besetzen</p>
          <Marquee pauseOnHover className="mask-fade-x [--duration:55s] [--gap:3rem]" repeat={3} aria-hidden="true">
            {STACK.map((s) => (
              <span key={s} className="flex items-center gap-3 whitespace-nowrap font-mono text-sm text-muted">
                <span className="size-1 rounded-full bg-primary/60" />{s}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Fakten */}
      <Container className="py-16">
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line lg:grid-cols-4">
          {FAKTEN.map((f, i) => (
            <BlurFade key={f.label} inView delay={i * 0.08} direction="up" className="bg-bg p-6 md:p-8">
              <dt className="sr-only">{f.label}</dt>
              <dd className="text-3xl font-semibold tracking-tight text-white md:text-5xl">
                {f.prefix && <span className="text-muted">{f.prefix}</span>}
                <NumberTicker value={f.wert} startValue={'start' in f ? f.start : 0} />
                {f.suffix && <span className="text-primary">{f.suffix}</span>}
              </dd>
              <dd className="mt-3 text-sm leading-snug text-muted" aria-hidden="true">{f.label}</dd>
            </BlurFade>
          ))}
        </dl>
      </Container>

      {/* Warum djiiga */}
      <section className="py-16 md:py-24">
        <Container>
          <SectionHeading
            eyebrow="Warum djiiga"
            title={<>Drei Unterschiede, <span className="text-muted">die Sie im ersten Gespräch merken.</span></>}
          />
          <BentoGrid className="mt-12 md:auto-rows-[20rem]">
            <BentoCard
              className="pt-52 md:col-span-2 md:pt-0"
              Icon={Radar}
              name="Direktansprache statt Stellenanzeige"
              description="Erfahrene Engineers bekommen in Berlin laufend Angebote und lesen keine Jobbörsen. Wir sprechen sie persönlich an, mit Bezug zur konkreten Rolle."
              background={<DirektanspracheBeam />}
              href="/headhunter-it-berlin"
              cta="So arbeiten wir als IT Headhunter"
            />
            <BentoCard
              Icon={BadgeEuro}
              name="Offene Preise"
              description={<>Im Regelfall <strong className="text-white">25 %</strong> des Bruttojahresgehalts, ab <strong className="text-white">15 %</strong> für kleinere Unternehmen. Fällig erst bei Einstellung, vorab schriftlich.</>}
              background={
                <div className="absolute right-6 top-6 select-none font-mono text-[7rem] font-semibold leading-none text-primary/[.07]">25%</div>
              }
              href="/fuer-unternehmen"
              cta="Konditionen im Detail"
            />
            <BentoCard
              Icon={ScanSearch}
              name="Ehrliche Markteinschätzung vor Suchbeginn"
              description="Passen Anforderungsprofil und Gehaltsband im Berliner Markt nicht zusammen, sagen wir das im ersten Gespräch. Nicht nach acht Wochen ohne Ergebnis."
              background={<DotPattern className="text-white/15 [mask-image:radial-gradient(260px_circle_at_top_right,white,transparent)]" />}
            />
            <BentoCard
              className="md:col-span-2"
              Icon={UserRoundCheck}
              name="Persönliche Vorauswahl statt Lebenslauf-Stapel"
              description="Wir sprechen mit jedem Kandidaten, bevor wir ihn vorstellen. Sie bekommen eine überschaubare Auswahl mit unserer Einschätzung, wo ein Profil stark ist und wo Sie genauer hinschauen sollten."
              background={
                <div className="absolute inset-x-6 top-6 hidden gap-3 md:flex" aria-hidden="true">
                  {['Passt fachlich', 'Gehalt im Band', 'Wechselbereit', 'Rückfrage: Rufbereitschaft'].map((t, i) => (
                    <span key={t} className={`rounded-full border px-3 py-1.5 font-mono text-[.7rem] ${i === 3 ? 'border-amber-400/30 text-amber-300/80' : 'border-signal/30 text-signal/90'}`}>{t}</span>
                  ))}
                </div>
              }
            />
          </BentoGrid>
        </Container>
      </section>

      {/* IT-Positionen */}
      <section className="relative border-y border-line bg-surface/30 py-20 md:py-28">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              eyebrow="Gesuchte IT Positionen"
              title="Rollen, die wir regelmäßig besetzen"
              lead="Zu jeder Rolle gibt es eine eigene Seite mit Aufgaben, Gehalt nach Erfahrung und Einstieg."
            />
            <ArrowLink to="/berufe">Alle Berufe im Überblick</ArrowLink>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {BERUFE.map((b, i) => {
              const I = berufIcon(b.icon);
              const s = b.gehalt.stufen;
              return (
                <BlurFade key={b.slug} inView delay={(i % 3) * 0.06} direction="up" className="h-full">
                  <Link to={`/berufe/${b.slug}`} className="group block h-full rounded-2xl">
                    <MagicCard className="h-full rounded-2xl [&>div:last-child]:h-full" gradientColor="#0b2a33" gradientFrom="#22d3ee" gradientTo="#8b5cf6" gradientSize={220}>
                      <div className="flex h-full items-center gap-4 p-5">
                        <span className="icon-chip transition-transform duration-300 group-hover:scale-105"><I className="size-5" aria-hidden="true" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium leading-snug text-white">{b.name}</span>
                          <span className="mt-0.5 block font-mono text-xs text-muted">{k(s[0].min)}–{k(s[s.length - 1].max)} € brutto</span>
                        </span>
                        <ArrowUpRight className="size-4 shrink-0 text-subtle transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
                      </div>
                    </MagicCard>
                  </Link>
                </BlurFade>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Ablauf */}
      <section className="py-20 md:py-28">
        <Container>
          <SectionHeading eyebrow="Ablauf" title="Wie eine Suche bei uns läuft" lead="Fünf Schritte, ein Ansprechpartner von Anfang bis Unterschrift." />
          <ol className="relative mt-14 grid gap-8 md:grid-cols-5 md:gap-6">
            <div className="pointer-events-none absolute top-5 right-[10%] left-[10%] hidden h-px bg-linear-to-r from-primary/0 via-primary/50 to-primary/0 md:block" aria-hidden="true" />
            {ABLAUF.map((s, i) => (
              <BlurFade as="li" key={s.titel} inView delay={i * 0.08} direction="up" className="relative">
                  <span className="relative z-10 inline-flex size-10 items-center justify-center rounded-xl border border-primary/40 bg-bg font-mono text-sm font-semibold text-primary shadow-[0_0_24px_-6px_rgb(34_211_238/.6)]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 text-lg font-semibold text-white">{s.titel}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{s.text}</p>
                </BlurFade>
            ))}
          </ol>
        </Container>
      </section>

      {/* Berliner Markt */}
      <section className="relative overflow-hidden border-y border-line py-20 md:py-28">
        <div className="glow-soft pointer-events-none absolute inset-0" aria-hidden="true" />
        <Container narrow className="relative text-center">
          <Eyebrow className="justify-center">Der Berliner Markt</Eyebrow>
          <p className="text-2xl font-medium leading-snug text-white md:text-[2rem] md:leading-[1.3]">
            Berlin ist der größte Technologiestandort Deutschlands. Erfahrene Entwicklerinnen und
            Entwickler bekommen hier regelmäßig Angebote, ohne selbst zu suchen.{' '}
            <span className="text-muted">Wer eine Stelle ausschreibt und wartet, erreicht nur einen kleinen Ausschnitt des Marktes.</span>
          </p>
          <p className="mt-8 text-sm text-muted">
            Bundesweit vergehen im Schnitt fast acht Monate, bis eine IT-Stelle besetzt ist (Bitkom, September 2026).{' '}
            <Link to="/blog/it-fachkraeftemangel-2026-berlin" className="link">Was messbar hilft</Link>
          </p>
        </Container>
      </section>

      {/* Inhaber */}
      <section className="py-20 md:py-28">
        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <SectionHeading
                eyebrow="Inhabergeführt"
                title="Sie sprechen mit den Inhabern. Von Anfang bis Unterschrift."
                lead="djiiga wird von den beiden Gründern geführt, die Ihre Suche auch selbst bearbeiten. Zusammen bringen sie mehr als 30 Jahre Erfahrung im IT-Recruiting mit."
              />
              <ArrowLink to="/ueber-uns" className="mt-8">Mehr über uns</ArrowLink>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {(['kamran', 'siamak'] as const).map((id, i) => {
                const a = AUTOREN[id];
                return (
                  <BlurFade key={id} inView delay={i * 0.1} direction="up">
                    <div className="card flex h-full flex-col items-center p-7 text-center">
                      <img src={a.portrait} alt={`Porträt von ${a.name}`} width={128} height={128} loading="lazy"
                           className="size-28 rounded-full object-cover ring-4 ring-primary/15 shadow-[0_20px_40px_-20px_rgb(34_211_238/.5)]" />
                      <p className="mt-5 text-lg font-semibold text-white">{a.name}</p>
                      <p className="text-sm text-primary">{a.rolle}</p>
                    </div>
                  </BlurFade>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* Blog */}
      <section className="border-t border-line bg-surface/30 py-20 md:py-28">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow="Blog" title="Aktuelles aus dem IT-Arbeitsmarkt" />
            <ArrowLink to="/blog">Alle Artikel</ArrowLink>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {posts.map((p, i) => (
              <BlurFade key={p.slug} inView delay={i * 0.08} direction="up" className="h-full">
                <Link to={`/blog/${p.slug}`} className="card card-hover group flex h-full flex-col overflow-hidden">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={p.imageUrl} srcSet={srcSet(p.imageUrl)} sizes="(min-width: 1024px) 380px, (min-width: 768px) 50vw, 100vw" alt={p.imageAlt ?? p.title} width={600} height={338} loading="lazy" decoding="async"
                         className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    <div className="absolute inset-0 bg-linear-to-t from-bg/70 to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-xs text-primary">{p.category} · {p.date}</p>
                    <h3 className="mt-3 line-clamp-3 text-lg font-semibold leading-snug text-white">{p.title}</h3>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-medium text-primary">
                      Artikel lesen <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </BlurFade>
            ))}
          </div>
        </Container>
      </section>

      {/* Abschluss */}
      <section className="relative overflow-hidden border-t border-line py-24 md:py-32">
        <RetroGrid className="opacity-60" angle={65} cellSize={56} lightLineColor="#22d3ee" darkLineColor="#22d3ee" opacity={0.25} />
        <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-bg via-bg/60 to-bg" aria-hidden="true" />
        <Container narrow className="relative text-center">
          <h2 className="text-4xl font-semibold text-white md:text-5xl">Eine offene IT-Position?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-muted">
            Schildern Sie uns die Rolle. Sie bekommen eine ehrliche Einschätzung, ob und wie schnell sie
            im Berliner Markt besetzbar ist. Das erste Gespräch ist unverbindlich.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <ButtonLink to="/kontakt?typ=unternehmen" arrow>Position besprechen</ButtonLink>
            <a href={`tel:${KONTAKT.telefonHref}`} className="btn-secondary"><Phone className="size-4" aria-hidden="true" />{KONTAKT.telefon}</a>
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-muted">
            <Search className="size-4 text-primary" aria-hidden="true" />
            Sie suchen eine neue Rolle? <Link to="/fuer-kandidaten" className="link">Für Kandidaten</Link>
          </p>
        </Container>
      </section>
    </div>
  );
};

export default Home;
